import { until } from '@vueuse/core';
import type { NavigationGuard } from 'vue-router';
import { useCurrentOrg } from '@/modules/current-org/api';
import { useTrial } from '@/modules/trial/api';
import type { TrialType } from '@/modules/trial/api';
import { clearModeLocalStorage } from '@/modules/mode/mode';

// Perform a hard redirect to /, and forces a user into their default mode
// This is used as an escape hatch to ensure that a user is safely returned into
// their default mode after attempting to access a route and mode that they don't
// have access to.
function navigationAndModeReset() {
  clearModeLocalStorage();
  window.localStorage.removeItem('redirectQuery');
  // Perform a hard redirect to /
  window.location.href = '/';
}

// If current org is not client, redirect to '/',
// else continue to requested route
export const requireClient: NavigationGuard = async (_to, _from, next) => {
  const { isClient } = useCurrentOrg();
  if (!(await isClient())) {
    navigationAndModeReset();
    return;
  }

  next();
};

// If current org is not supplier, redirect to '/',
// else continue to requested route
export const requireSupplier: NavigationGuard = async (_to, _from, next) => {
  const { isSupplier } = useCurrentOrg();
  if (!(await isSupplier())) {
    navigationAndModeReset();
    return;
  }
  next();
};

// Orgs with a type are sent back to '/'. A client restricted org has no client
// side, so a restricted client-only org counts as having no type.
export const requireNoType: NavigationGuard = async (_to, _from, next) => {
  const { isClient, isSupplier, isFederated, isClientRestricted } = useCurrentOrg();
  const hasClientSide = (await isClient()) && !(await isClientRestricted());
  if (hasClientSide || (await isSupplier()) || (await isFederated())) {
    navigationAndModeReset();
    return;
  }
  next();
};

// If current org is not federated, redirect to '/',
// else continue to requested route.
// May need to consider trial status at a later stage.
export const requireFederated: NavigationGuard = async (_to, _from, next) => {
  const { isFederated } = useCurrentOrg();
  if (!(await isFederated())) {
    navigationAndModeReset();
    return;
  }

  next();
};

// If current org is federated, redirect to '/',
// else continue to requested route.
// May need to consider trial status at a later stage.
export const requireNotFederated: NavigationGuard = async (_to, _from, next) => {
  const { isFederated } = useCurrentOrg();
  if (await isFederated()) {
    navigationAndModeReset();
    return;
  }

  next();
};

// Checks if org is any of the given types.
// Does not require that mode to be the current one.
// In the case of client being one of the required types, it does NOT check for trial status.
export function requireAnyType(typeList: ('supplier' | 'client' | 'federated')[]) {
  return (async (_to, _from, next) => {
    const { isClient, isSupplier, isFederated } = useCurrentOrg();

    if (
      (typeList.includes('client') && (await isClient())) ||
      (typeList.includes('supplier') && (await isSupplier())) ||
      (typeList.includes('federated') && (await isFederated()))
    ) {
      next();
      return;
    }

    navigationAndModeReset();
  }) satisfies NavigationGuard;
}

// A client restricted org has no client side. It is sent to the first mode it
// does have, in the same order the mode resolver uses, or to getting started
// to become a supplier.
export const requireClientNotRestricted: NavigationGuard = async (_to, _from, next) => {
  const { isSupplier, isFederated, isClientRestricted } = useCurrentOrg();
  if (!(await isClientRestricted())) {
    next();
    return;
  }
  if (await isFederated()) {
    next('/f/');
    return;
  }
  next((await isSupplier()) ? '/s/' : { name: 'getting-started' });
};

export const requireTrialType =
  (trialType: TrialType): NavigationGuard =>
  async (_to, _from, next) => {
    const { trial, isOnTrialType } = useTrial();

    if (trial.value === undefined) {
      await until(trial).toMatch((c) => typeof c !== 'undefined');
    }

    if (!isOnTrialType(trialType)) {
      next({ name: 'client-dashboard' });
      return;
    }

    next();
  };
